/**
 * Pure calendar-realism helpers for the diary (WS7 phase 1). No React, no Date, no DOM — they take
 * the day's scheduling facts (time-off, hour-exceptions) for one staff member and a viewed date and
 * resolve them into the diary's wall-clock minute model: is this column off today, what's the label,
 * what's the adjusted working band, and where (if anywhere) does a break stripe sit.
 *
 * Keeping this pure (like layout.ts) is what protects the <50ms interaction budget — the React layer
 * calls these once per column in a useMemo, never per pointer-move.
 */
import type { HourException, TimeOff, TimeOffReason } from '@salonomy/types';

const REASON_LABEL: Record<TimeOffReason, string> = {
  holiday: 'holiday',
  sick: 'sick',
  training: 'training',
  other: 'time off',
};

/** "HH:MM" wall-clock → minutes from midnight, or null for blanks. */
export function timeToMin(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = /^([01]?\d|2[0-3]):([0-5]\d)/.exec(t);
  if (!m) return null;
  return +m[1]! * 60 + +m[2]!;
}

/** A date "YYYY-MM-DD" falls inside an inclusive [startDate, endDate] range. */
export function dateInRange(date: string, startDate: string, endDate: string): boolean {
  return date >= startDate && date <= endDate;
}

export interface BreakBand {
  startMin: number;
  endMin: number;
}

/**
 * The resolved off/working state of one staff column on the viewed date. `off` greys the whole
 * column; otherwise `band` (when present) is the adjusted working window and `break_` (when present)
 * is a mid-day break stripe. `off` always wins — an is_closed exception or any covering time-off.
 */
export interface ColumnCalendar {
  off: boolean;
  offLabel: string | null;
  band: { startMin: number; endMin: number } | null;
  break_: BreakBand | null;
}

/**
 * Resolve one staff member's calendar for `date` from their time-off entries and hour-exceptions.
 * Time-off and an is_closed exception both mean "off". A non-closed exception with custom hours
 * yields an adjusted band; a break (both ends present and ordered) yields a stripe. Pure + cheap.
 */
export function resolveColumnCalendar(
  date: string,
  timeOff: TimeOff[],
  exceptions: HourException[],
): ColumnCalendar {
  const off = timeOff.find((t) => dateInRange(date, t.startDate, t.endDate));
  const exc = exceptions.find((e) => e.date === date);

  if (off) {
    return { off: true, offLabel: `Off — ${REASON_LABEL[off.reason]}`, band: null, break_: null };
  }
  if (exc?.isClosed) {
    return { off: true, offLabel: 'Off', band: null, break_: null };
  }

  let band: ColumnCalendar['band'] = null;
  let break_: BreakBand | null = null;
  if (exc) {
    const startMin = timeToMin(exc.startTime);
    const endMin = timeToMin(exc.endTime);
    if (startMin != null && endMin != null && endMin > startMin) {
      band = { startMin, endMin };
    }
    const bStart = timeToMin(exc.breakStart);
    const bEnd = timeToMin(exc.breakEnd);
    if (bStart != null && bEnd != null && bEnd > bStart) {
      break_ = { startMin: bStart, endMin: bEnd };
    }
  }
  return { off: false, offLabel: null, band, break_ };
}
